import type {
    KpiAxisConfigurationMetaDefaults,
    KpiAxisControlsMeta,
} from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import {
    type KpiBinLabelAffixes,
    toKpiBinLabelAffixesFromUnitRep,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { DEFAULT_UNIFORM_BIN_COUNT, MAX_KPI_BIN_COUNT } from 'constants/ppa';

import { findNestedControl, getXAxisColumnOptions, getXAxisColumnOptionsSection } from './packArchitectureMeta.helpers';
import type { PpaMetaControlNode, PpaMetaOptionItem } from './packArchitectureMeta.types';

const normalizeKpiId = (kpiId: string) => kpiId.toLowerCase().replace(/^ps_/, '');

const inferKpiBinLabelAffixes = (kpiId: string, currencyPrefix: string): KpiBinLabelAffixes => {
    const normalized = normalizeKpiId(kpiId);

    if (normalized.includes('price')) {
        return { prefix: currencyPrefix };
    }
    if (normalized.includes('ounce')) {
        return { suffix: 'oz' };
    }
    if (normalized === 'count') {
        return { suffix: 'ct' };
    }

    return {};
};

/** Resolves PA-01 unit representation for KPI axis bin preview labels. */
export const getKpiBinLabelAffixesFromColumnOption = (
    option?: PpaMetaOptionItem | null,
    currencyPrefix = '',
): KpiBinLabelAffixes => {
    if (!option) return {};

    const prefix = option.valuePrefix ?? option.value_prefix;
    const suffix = option.valueSuffix ?? option.value_suffix;
    if (prefix || suffix) {
        return { prefix, suffix };
    }

    const unitRep = option.unit_rep?.trim();
    if (unitRep) {
        return toKpiBinLabelAffixesFromUnitRep(unitRep, currencyPrefix);
    }

    return option.id ? inferKpiBinLabelAffixes(option.id, currencyPrefix) : {};
};

export const getKpiBinLabelAffixesForXAxis = (
    xAxisId: string,
    xAxisConfig?: PpaMetaControlNode,
    currencyPrefix = '',
): KpiBinLabelAffixes => {
    if (!xAxisId) return {};

    const option = getXAxisColumnOptions(xAxisConfig).find((item) => item.id === xAxisId);
    return getKpiBinLabelAffixesFromColumnOption(option, currencyPrefix);
};

const toNumericDefault = (value: boolean | number | null | undefined, fallback: number) =>
    typeof value === 'number' ? value : fallback;

const getColumnOptionControlRoots = (xAxis?: PpaMetaControlNode, sectionId = 'column_options') => {
    const section = getXAxisColumnOptionsSection(xAxis, sectionId);
    return section?.options ?? [];
};

const toKpiInputMeta = (
    node: PpaMetaControlNode | undefined,
    labelFallback: string,
    valueFallback: number,
    maxValueFallback?: number,
) => ({
    label: node?.label ?? labelFallback,
    defaultValue: toNumericDefault(node?.default_value, valueFallback),
    ...(maxValueFallback !== undefined
        ? { maxValue: toNumericDefault(node?.max, maxValueFallback) }
        : {}),
});

const buildKpiRangeControlsMetaFromNode = (rangeNode?: PpaMetaControlNode): KpiAxisControlsMeta['range'] => {
    const rangeSlider = rangeNode?.options?.[0];
    const rangeMinInput = rangeSlider?.options?.find((option) => option.id === 'min');
    const rangeMaxInput = rangeSlider?.options?.find((option) => option.id === 'max');

    return {
        label: rangeNode?.label ?? 'Range',
        step: rangeSlider?.step ?? 1,
        minInput: toKpiInputMeta(rangeMinInput, 'Min.', 0),
        maxInput: toKpiInputMeta(rangeMaxInput, 'Max.', 100),
    };
};

const buildKpiBinsControlsMetaFromNode = (binsNode?: PpaMetaControlNode): KpiAxisControlsMeta['bins'] => {
    const uniformToggle = findNestedControl(binsNode?.options ?? null, 'uniform');
    const customToggle = findNestedControl(binsNode?.options ?? null, 'custom');
    const uniformCountInput = findNestedControl(uniformToggle?.options ?? null, 'count');
    const uniformSizeInput = findNestedControl(uniformToggle?.options ?? null, 'size');
    const customBreakPointInput = findNestedControl(binsNode?.options ?? null, 'break_point');

    return {
        label: binsNode?.label ?? 'Bins',
        uniform: {
            label: uniformToggle?.label ?? 'Uniform',
            defaultSelected: uniformToggle?.default_value !== false,
            count: toKpiInputMeta(uniformCountInput, 'Count', DEFAULT_UNIFORM_BIN_COUNT, MAX_KPI_BIN_COUNT),
            size: toKpiInputMeta(uniformSizeInput, 'Size', 1),
        },
        custom: {
            label: customToggle?.label ?? 'Custom',
            defaultSelected: Boolean(customToggle?.default_value),
            breakPoint: toKpiInputMeta(customBreakPointInput, 'Break Point', 0),
        },
    };
};

export const buildKpiAxisControlsMeta = (
    xAxis?: PpaMetaControlNode,
    sectionId = 'column_options',
): KpiAxisControlsMeta | null => {
    const controlRoots = getColumnOptionControlRoots(xAxis, sectionId);
    if (!controlRoots.length) return null;

    const rangeNode = findNestedControl(controlRoots, 'range');
    const binsNode = findNestedControl(controlRoots, 'bins');
    if (!rangeNode && !binsNode) return null;

    return {
        range: buildKpiRangeControlsMetaFromNode(rangeNode),
        bins: buildKpiBinsControlsMetaFromNode(binsNode),
    };
};

export const getKpiAxisConfigurationDefaultsFromMeta = (
    controlsMeta: KpiAxisControlsMeta | null | undefined,
): KpiAxisConfigurationMetaDefaults | undefined => {
    if (!controlsMeta) return undefined;

    const { uniform } = controlsMeta.bins;
    const binningMethod = uniform.defaultSelected ? 'uniform' : 'custom';
    const countDefault = uniform.count.defaultValue;
    const binCount = countDefault > 0 ? countDefault : DEFAULT_UNIFORM_BIN_COUNT;
    const maxBinCount = uniform.count.maxValue ?? MAX_KPI_BIN_COUNT;

    return { binningMethod, binCount, maxBinCount };
};
